/**
 * /terms, /privacy and /license-agreement — the public legal pages.
 *
 * ONE COMPONENT, MULTIPLE ROUTES. Each path renders this with a different document id, so
 * the pages cannot drift apart in typography, in their last-updated line, or in their
 * cross-links.
 *
 * PUBLIC, LIKE /share AND /license/verify. Mounted as a sibling of the app shell rather
 * than inside it — §4 requires these to be readable without an account, and the whole
 * point of a terms link in a signup form is that the person clicking it does not have one
 * yet. So there is no sidebar and no player bar, and the page carries its own brand
 * header exactly as routes/Share.tsx does.
 *
 * NOT A NEW DESIGN SYSTEM — the brief rules that out explicitly, and there was no reason
 * to want one. The type scale, the colours and the section rhythm are lifted from
 * routes/Agreement.tsx, which is the app's existing legal page: a 16px Space Grotesk
 * <h1>, an 11px meta line, 13px Inter <h2> clause headings, 12px `c.text2` body at 1.7
 * line-height, 14px between clauses. The structural additions are the ones the existing
 * agreement page does not need: a brand header (it renders inside the shell, these do
 * not) and cross-links to the sibling documents.
 *
 * READABLE AT 380px, and by construction rather than by a media query: `.lyx-legal-page`
 * is the same centred-column pattern as `.lyx-share-page` — a max-width and side padding,
 * so it is one column at every width and nothing has to be rearranged. It reuses the
 * 560px breakpoint that already exists for the padding step. No new breakpoint: the
 * stylesheet's max-width set is pinned to five values by styles/responsive.test.ts, and a
 * sixth added for a text page would be the start of the pattern that test exists to
 * prevent.
 */
import { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Logo } from '../components/ui';
import { LEGAL_DOCUMENTS, LEGAL_DOCUMENT_ORDER } from '../data/legal';
import { c } from '../styles/theme';
import type { LegalDocumentId } from '../types/domain';
import { paths } from './paths';

export interface LegalProps {
  /** Which legal document this route renders. */
  id: LegalDocumentId;
}

/** "3 August 2026" from an ISO date, in the reader's locale. */
function updatedLabel(iso: string, version: string): string {
  const date = new Date(iso);
  const formatted = Number.isNaN(date.getTime())
    ? iso
    : date.toLocaleDateString(undefined, { day: 'numeric', month: 'long', year: 'numeric' });
  return `Last updated ${formatted} · Version ${version}`;
}

export default function Legal({ id }: LegalProps) {
  const doc = LEGAL_DOCUMENTS[id];

  /*
    THE ONLY document.title CALL IN THE APPLICATION, and it is here rather than in a
    shared helper because these are the only four pages that need one. §4 asks for page
    titles, and index.html has a single static <title> that every route inherits — which
    is acceptable for an app behind a login and wrong for a public legal document, where
    the title is what a browser tab, a bookmark and a shared link all show. A visitor with
    four of these open otherwise sees four identical tabs.

    Restored on unmount so navigating back into the app does not leave "Privacy Policy" in
    the tab. Read at effect time rather than captured at module load, so a nested
    navigation between two legal pages restores the right one.
  */
  useEffect(() => {
    const previous = document.title;
    document.title = `${doc.title} · Lyrixsa AI`;
    return () => {
      document.title = previous;
    };
  }, [doc.title]);

  return (
    <div className="lyx-legal-page">
      {/* ---- brand header. Not the app's sidebar: this reader may have no account. ----

           THE MARK IS NOW THE APP'S. This header used to draw an amber badge holding
           lucide's `Scale` glyph — a pair of legal balances — so /terms and /privacy were
           branded with a different icon than every other screen. A reader arriving here
           from an email link has only this to tell them whose terms they are reading, and
           "some amber square with scales in it" is not that. Same lockup as the sidebar
           and the login screen now, at the size this page's type is set to. */}
      <header style={{ display: 'flex', alignItems: 'center', marginBottom: 22 }}>
        <Logo size={26} />
      </header>

      <h1
        style={{
          fontFamily: 'Space Grotesk',
          fontWeight: 600,
          fontSize: 16,
          margin: '0 0 4px',
          color: c.text1,
        }}
      >
        {doc.title}
      </h1>
      <p style={{ fontSize: 12.5, color: c.text2, margin: '0 0 6px', lineHeight: 1.6 }}>
        {doc.summary}
      </p>
      {/* §4 asks for last-updated info. Version alongside the date because the consent
          record stores the version the user accepted, so the two have to be visible
          together for that record to mean anything. */}
      {/* text2, not the text3 that routes/Agreement.tsx uses for its equivalent line:
          text3 is below AA on every surface (styles/contrast.test.ts), and the version
          here is the thing a consent record refers to, so it has to be legible. */}
      <p style={{ fontSize: 11, color: c.text2, margin: '0 0 18px' }}>
        {updatedLabel(doc.updated, doc.version)}
      </p>

      <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
        {doc.clauses.map((clause) => (
          <section key={clause.title}>
            <h2 style={{ fontWeight: 600, fontSize: 13, margin: '0 0 4px', fontFamily: 'Inter' }}>
              {clause.title}
            </h2>
            {clause.paragraphs.map((paragraph, i) => (
              <p
                // Index keys: the paragraphs of a clause are a fixed ordered list that
                // nothing reorders, inserts into or filters at runtime.
                key={i}
                style={{
                  fontSize: 12,
                  color: c.text2,
                  margin: i === 0 ? 0 : '8px 0 0',
                  lineHeight: 1.7,
                }}
              >
                {paragraph}
              </p>
            ))}
          </section>
        ))}
      </div>

      {/* ---- the sibling legal documents. ------------------------------------------ */}
      <nav
        aria-label="Legal documents"
        style={{
          display: 'flex',
          flexWrap: 'wrap',
          gap: '8px 16px',
          marginTop: 28,
          paddingTop: 16,
          borderTop: `1px solid ${c.border}`,
        }}
      >
        {LEGAL_DOCUMENT_ORDER.map((other) => {
          const isCurrent = other === id;
          return (
            <Link
              key={other}
              to={paths.legal(other)}
              // The current document is still listed, and still a link — dropping it
              // would make the row a different length on each page and lose the sense
              // that these are one set. aria-current says which one you are on.
              aria-current={isCurrent ? 'page' : undefined}
              style={{
                fontSize: 12,
                color: isCurrent ? c.text1 : c.accent,
                textDecoration: 'none',
                fontWeight: isCurrent ? 500 : 400,
              }}
            >
              {LEGAL_DOCUMENTS[other].title}
            </Link>
          );
        })}
      </nav>
    </div>
  );
}
